import React from 'react';
import {
    Alert,
    Button,
    Checkbox,
    DialogV2,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    UpdateEntityDialog,
} from 'c-components';
import type { Campaign } from 'c-sdk';
import { Paper, Table, Stack, TableContainer, Typography, CircularProgress } from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import CampaignShareDialog from 'c-main/Pages/Campaign/CampaignOverviewPage/CampaignShareDialog';
import { PcaDialog } from 'c-main/Components/Campaign/PcaDialog/PcaDialog';
import PptPCADialog from 'c-main/Components/Campaign/PptPca/PptPCADialog';
import CampaignOverviewEdit from 'c-main/Pages/Campaign/CampaignOverviewPage/CampaignOverviewEdit';
import CreativeScheduleCheckDialog from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar/CreativeScheduleCheckDialog';
import type { UseBoolean } from 'react-hanger';
import { CampaignBookingSummaryDialog } from 'c-main/Components';
import SummaryCustomizationDialog from 'c-main/Components/Campaign/CampaignAnalytics/SummaryCustomizationDialog';
import ReportingInputsForm from './ReportingInputsForm';

interface DialogsContainerProps {
    campaign: Campaign;
    id: number;
    source: string;
    editCampaignDialogState: UseBoolean;
    campaignShareDialogState: UseBoolean;
    campaignBookingSummaryState: UseBoolean;
    pcaState: UseBoolean;
    pptPcaState: UseBoolean;
    iOIdsState: UseBoolean;
    happyDemicsState: UseBoolean;
    insertionOrderIds: string;
    setInsertionOrderIds: (value: string) => void;
    updateDspInsertionOrderIds: () => Promise<void>;
    isLoading: boolean;
    error: any;
    currentHappydemicsId: number | null;
    listData: any;
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    filteredListData: any[];
    checkboxHandler: (e: React.ChangeEvent<HTMLInputElement>, id: number) => void;
    happyDemicsChoice: number | null;
    addHappyDemicsToCampaign: () => Promise<void>;
    removeHappyDemicsFromCampaign: () => Promise<void>;
    defaultData: any;
    reportingInputsState: UseBoolean;
    creativeScheduleCheckState: UseBoolean;
}

const DialogsContainer: React.FC<DialogsContainerProps> = ({
    campaign,
    id,
    source,
    editCampaignDialogState,
    campaignShareDialogState,
    campaignBookingSummaryState,
    pcaState,
    pptPcaState,
    iOIdsState,
    happyDemicsState,
    insertionOrderIds,
    setInsertionOrderIds,
    updateDspInsertionOrderIds,
    isLoading,
    error,
    currentHappydemicsId,
    listData,
    searchTerm,
    setSearchTerm,
    filteredListData,
    checkboxHandler,
    happyDemicsChoice,
    addHappyDemicsToCampaign,
    removeHappyDemicsFromCampaign,
    defaultData,
    reportingInputsState,
    creativeScheduleCheckState,
}) => {
    const t = useCommonTranslation();
    const campaignType = campaign.type;

    const campaignHasDspReporting =
        id === 8896
            ? false
            : campaignType === 'linear' || campaignType === 'default'
            ? id === 9464 || id === 10160
            : true;

    return (
        <>
            <UpdateEntityDialog<Campaign>
                entityName="Campaign"
                entityId={campaign.id}
                title="Modules.Main.Campaigns.Overview.ActionLabels.reportingInputs"
                defaultData={defaultData}
                show={reportingInputsState.value}
                form={
                    <ReportingInputsForm
                        campaign={campaign}
                        campaignHasDspReporting={campaignHasDspReporting}
                    />
                }
                onClose={reportingInputsState.setFalse}
            />
            <SummaryCustomizationDialog campaign={campaign} />
            <CampaignShareDialog
                campaign={campaign}
                open={campaignShareDialogState.value}
                onClose={campaignShareDialogState.setFalse}
            />
            <CampaignBookingSummaryDialog
                campaign={campaign}
                open={campaignBookingSummaryState.value}
                onClose={campaignBookingSummaryState.setFalse}
            />
            <PcaDialog id={campaign.id} pcaState={pcaState} campaign={campaign} />
            <PptPCADialog
                id={campaign.id}
                pcaState={pptPcaState}
                isLinear={campaignType === 'linear' || campaignType === 'default'}
                campaignStartDate={campaign?.date_start ?? null}
                campaignEndDate={campaign?.date_end ?? null}
            />
            <CampaignOverviewEdit id={id} dialogState={editCampaignDialogState} source={source} />
            <CreativeScheduleCheckDialog
                campaignId={campaign.id}
                dialogState={creativeScheduleCheckState}
            />
            <DialogV2 onClose={iOIdsState.setFalse} open={iOIdsState.value}>
                <Stack gap={2}>
                    <TextField
                        multiline
                        value={insertionOrderIds}
                        onChange={e => setInsertionOrderIds(e.target.value)}
                    />
                    <Button onClick={updateDspInsertionOrderIds}>
                        {isLoading ? (
                            <CircularProgress />
                        ) : (
                            t('Modules.Main.Campaigns.Overview.ActionLabels.apply')
                        )}
                    </Button>
                    {error && <Alert severity="error">{String(error)}</Alert>}
                </Stack>
            </DialogV2>
            <DialogV2
                onClose={happyDemicsState.setFalse}
                open={happyDemicsState.value}
                title={t('Modules.Main.Campaigns.Overview.HappyDemics.title')}
                maxWidth="xl"
            >
                <Stack gap={2}>
                    {currentHappydemicsId !== null && (
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Stack gap={1} flexGrow={1}>
                                <Typography fontWeight="bold">
                                    {t('Modules.Main.Campaigns.Overview.HappyDemics.selected')}
                                </Typography>
                                {listData?.data?.data?.find(
                                    item => item.id === currentHappydemicsId,
                                )?.name || 'N/A'}
                            </Stack>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={removeHappyDemicsFromCampaign}
                            >
                                {t('Modules.Main.Campaigns.Overview.HappyDemics.remove')}
                            </Button>
                        </Stack>
                    )}
                    <TextField
                        placeholder={t('Modules.Main.Campaigns.Overview.HappyDemics.search')}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        fullWidth
                        size="small"
                    />
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t('Modules.Main.Campaigns.Overview.HappyDemics.selectCol')}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            'Modules.Main.Campaigns.Overview.HappyDemics.happydemicsCampaign',
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            'Modules.Main.Campaigns.Overview.HappyDemics.happyDemicsId',
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredListData.map(item => (
                                    <TableRow
                                        key={item.id}
                                        selected={happyDemicsChoice === item.id}
                                    >
                                        <TableCell>
                                            <Checkbox
                                                checked={happyDemicsChoice === item.id}
                                                onChange={e => checkboxHandler(e, item.id)}
                                            />
                                        </TableCell>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.id}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Button onClick={addHappyDemicsToCampaign}>
                        {t('Modules.Main.Campaigns.Overview.HappyDemics.submit')}
                    </Button>
                </Stack>
            </DialogV2>
        </>
    );
};
export default DialogsContainer;
